import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Button, Card, CardContent, Alert, CircularProgress, Box, Chip, Link as MuiLink, Typography, IconButton, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { Link } from 'react-router';
import { useApplicationContext } from 'ApplicationContext';
import { FileTypeLicenseResponse, FileTypeLicenseStatus } from 'apiClient/Api';
import { useDocumentTitle } from 'Hooks/useDocumentTitle';
import { CheckCircle, Cancel, HourglassEmpty, Block } from '@mui/icons-material';
import dayjs from 'dayjs';
import Title from 'Components/Title';

/**
 * AdminRequestedLicensesNew component for managing requested licenses
 * @returns {JSX.Element} The AdminRequestedLicensesNew page
 */
const AdminRequestedLicensesNew: React.FC = (): JSX.Element => {
  useDocumentTitle("base.pages.requestedLicenses");
  const context = useApplicationContext();
  const { t } = useTranslation();
  
  const [licenses, setLicenses] = useState<FileTypeLicenseResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({ deviceHardwareId: false });

  /**
   * Fetches requested licenses from the API
   */
  const fetchLicenses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await context.api.api.licenseGetRequestedLicenses();
      if (response && response.data) {
        setLicenses(response.data);
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.title || err?.message || t('error.fetchFailed');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [context.api.api, t]);

  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  /**
   * Gets the row ID for DataGrid
   * @param {FileTypeLicenseResponse} row - The license row
   * @returns {string} The row identifier
   */
  const getRowId = (row: FileTypeLicenseResponse): string => {
    return row.id ?? '';
  };

  /**
   * Gets the status label for a license status
   * @param {FileTypeLicenseStatus | undefined} status - The license status
   * @returns {string} The translated status label
   */
  const getStatusLabel = (status?: FileTypeLicenseStatus): string => {
    switch (status) {
      case FileTypeLicenseStatus.Draft:
        return t('requestedLicensesNew.status.draft');
      case FileTypeLicenseStatus.Requested:
        return t('requestedLicensesNew.status.requested');
      case FileTypeLicenseStatus.Approved:
        return t('requestedLicensesNew.status.approved');
      case FileTypeLicenseStatus.Active:
        return t('requestedLicensesNew.status.active');
      case FileTypeLicenseStatus.Rejected:
        return t('requestedLicensesNew.status.rejected');
      case FileTypeLicenseStatus.Expired:
        return t('requestedLicensesNew.status.expired');
      default:
        return '-';
    }
  };

  /**
   * Gets the icon and color for a license status
   * @param {FileTypeLicenseStatus | undefined} status - The license status
   * @returns {object} The icon component and color
   */
  const getStatusIcon = (status?: FileTypeLicenseStatus) => {
    switch (status) {
      case FileTypeLicenseStatus.Requested:
        return { icon: <HourglassEmpty fontSize="small" />, color: 'warning' as const };
      case FileTypeLicenseStatus.Approved:
        return { icon: <CheckCircle fontSize="small" />, color: 'success' as const };
      case FileTypeLicenseStatus.Active:
        return { icon: <CheckCircle fontSize="small" />, color: 'primary' as const };
      case FileTypeLicenseStatus.Rejected:
        return { icon: <Cancel fontSize="small" />, color: 'error' as const };
      case FileTypeLicenseStatus.Expired:
        return { icon: <Block fontSize="small" />, color: 'default' as const };
      default:
        return { icon: null, color: 'default' as const };
    }
  };

  /**
   * Approves a license
   * @param {string} licenseId - The license ID to approve
   */
  const handleApproveLicense = async (licenseId: string): Promise<void> => {
    try {
      await context.api.api.licenseApproveDeviceLicense(licenseId);
      fetchLicenses();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.title || err?.message || t('error.fetchFailed');
      setError(errorMessage);
    }
  };

  /**
   * Rejects a license
   * @param {string} licenseId - The license ID to reject
   */
  const handleRejectLicense = async (licenseId: string): Promise<void> => {
    try {
      await context.api.api.licenseRejectDeviceLicense(licenseId, {});
      fetchLicenses();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.title || err?.message || t('error.fetchFailed');
      setError(errorMessage);
    }
  };

  const columns: GridColDef<FileTypeLicenseResponse>[] = [
    {
      field: 'deviceName',
      headerName: t('requestedLicensesNew.columns.deviceName'),
      flex: 1,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams<FileTypeLicenseResponse>) =>
        params.row.deviceId ? (
          <MuiLink
            component={Link}
            to={`/device/${params.row.deviceId}`}
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
          >
            {params.row.deviceName ?? '-'}
          </MuiLink>
        ) : (
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {params.row.deviceName ?? '-'}
          </Typography>
        ),
    },
    {
      field: 'supportCode',
      headerName: t('requestedLicensesNew.columns.supportCode'),
      flex: 1,
      minWidth: 160,
      renderCell: (params: GridRenderCellParams<FileTypeLicenseResponse>) =>
        params.row.supportCode ? (
          <MuiLink
            component={Link}
            to={`/deviceManagement/${params.row.supportCode}`}
            underline="hover"
            sx={{ fontFamily: 'monospace', fontSize: '0.85rem', display: 'flex', alignItems: 'center', height: '100%' }}
          >
            {params.row.supportCode}
          </MuiLink>
        ) : (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', display: 'flex', alignItems: 'center', height: '100%' }}>
            -
          </Typography>
        ),
    },
    {
      field: 'customerName',
      headerName: t('requestedLicensesNew.columns.customerName'),
      flex: 1,
      minWidth: 180,
      valueGetter: (_, row) => row.customerName ?? '-',
    },
    {
      field: 'deviceHardwareId',
      headerName: t('requestedLicensesNew.columns.deviceHardwareId'),
      flex: 1,
      minWidth: 160,
      valueGetter: (_, row) => row.deviceHardwareId ?? '-',
    },
    {
      field: 'fileTypeName',
      headerName: t('requestedLicensesNew.columns.fileTypeName'),
      flex: 1,
      minWidth: 180,
      valueGetter: (_, row) => row.fileTypeName ?? '-',
    },
    {
      field: 'requestedBy',
      headerName: t('requestedLicensesNew.columns.requestedBy'),
      flex: 1,
      minWidth: 160,
      valueGetter: (_, row) => row.requestedBy?.name ?? '-',
    },
    {
      field: 'issuedAt',
      headerName: t('requestedLicensesNew.columns.issuedAt'),
      flex: 1,
      minWidth: 160,
      valueGetter: (_, row) => row.issuedAt ? dayjs(row.issuedAt).format('DD.MM.YYYY HH:mm') : '-',
    },
    {
      field: 'expiresAt',
      headerName: t('requestedLicensesNew.columns.expiresAt'),
      flex: 1,
      minWidth: 160,
      valueGetter: (_, row) => row.expiresAt ? dayjs(row.expiresAt).format('DD.MM.YYYY') : '-',
    },
    {
      field: 'actions',
      headerName: t('requestedLicensesNew.columns.actions'),
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<FileTypeLicenseResponse>) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
          {params.row.status === FileTypeLicenseStatus.Requested && (
            <>
              <Tooltip title={t('requestedLicensesNew.actions.approve')}>
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => params.row.id && handleApproveLicense(params.row.id)}
                >
                  <CheckCircle fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('requestedLicensesNew.actions.reject')}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => params.row.id && handleRejectLicense(params.row.id)}
                >
                  <Cancel fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      <Title>{t('requestedLicensesNew.title')}</Title>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Button onClick={fetchLicenses} disabled={loading}>
              {t('requestedLicensesNew.refresh')}
            </Button>
          </Box>

          <DataGrid
            rows={licenses}
            columns={columns}
            getRowId={getRowId}
            loading={loading}
            autoHeight
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
            }}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
            sx={{
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        </CardContent>
      </Card>
    </Container>
  );
};

export default AdminRequestedLicensesNew;
